import type { ImageOptions } from './imgix';

const CDAAS_SERVING_DOMAIN = 'static-dev.americanexpress.com';
const CDAAS_ORIGIN_DOMAIN = 'cdaas-dev.americanexpress.com';
const CDAAS_PATH_PREFIX = '/gcs/tock-public-assets-e1';

const PLACEHOLDER_GIF =
  'data:image/gif;base64,R0lGODlhAQABAID/AMDAwAAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

// Matches a CDaaS serving URL with directives: /{prefix}/{directives}/{asset-path}
// A directive segment is comma-joined tokens each containing an underscore (e.g. "c_fill,w_576,h_384").
const CDAAS_SERVING_PATH_PATTERN = new RegExp(
  `^${CDAAS_PATH_PREFIX}/([^/]*_[^/]*)/(.+)$`
);

// Matches a CDaaS URL without directives: /{prefix}/{asset-path}
const CDAAS_BARE_PATH_PATTERN = new RegExp(`^${CDAAS_PATH_PREFIX}/(.+)$`);

interface DirectiveOptions {
  width?: number;
  height?: number;
  q?: number;
  centerCrop?: boolean;
}

export function hasCdaasUrl(url?: string): boolean {
  if (!url) {
    return false;
  }
  return url.includes(CDAAS_SERVING_DOMAIN) || url.includes(CDAAS_ORIGIN_DOMAIN);
}

function extractAssetPath(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.host !== CDAAS_SERVING_DOMAIN && parsed.host !== CDAAS_ORIGIN_DOMAIN) {
    return null;
  }
  const withDirectives = parsed.pathname.match(CDAAS_SERVING_PATH_PATTERN);
  if (withDirectives) {
    return withDirectives[2];
  }
  const bare = parsed.pathname.match(CDAAS_BARE_PATH_PATTERN);
  if (bare) {
    return bare[1];
  }
  return null;
}

function extractExistingDirectives(url: string): DirectiveOptions {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return {};
  }
  if (parsed.host !== CDAAS_SERVING_DOMAIN) {
    return {};
  }
  const match = parsed.pathname.match(CDAAS_SERVING_PATH_PATTERN);
  if (!match) {
    return {};
  }
  const result: DirectiveOptions = {};
  match[1].split(',').forEach((part) => {
    if (part === 'c_fill') {
      result.centerCrop = true;
      return;
    }
    const [key, value] = part.split('_');
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return;
    }
    if (key === 'w') {
      result.width = numericValue;
    } else if (key === 'h') {
      result.height = numericValue;
    } else if (key === 'q') {
      result.q = numericValue;
    }
  });
  return result;
}

function buildDirectiveString(opts: DirectiveOptions): string {
  const parts: string[] = [];
  if (opts.centerCrop) {
    parts.push('c_fill');
  }
  if (opts.width !== undefined) {
    parts.push(`w_${opts.width}`);
  }
  if (opts.height !== undefined) {
    parts.push(`h_${opts.height}`);
  }
  if (opts.q !== undefined) {
    parts.push(`q_${opts.q}`);
  }
  return parts.join(',');
}

function buildUrl(assetPath: string, directives: string): string {
  const cleanPath = assetPath.startsWith('/') ? assetPath.substring(1) : assetPath;
  let url = `https://${CDAAS_SERVING_DOMAIN}${CDAAS_PATH_PREFIX}/`;
  if (directives) {
    url += `${directives}/`;
  }
  url += cleanPath;
  return url;
}

export function getAutoFormatUrl(url?: string, options?: ImageOptions): string {
  if (!url) {
    return PLACEHOLDER_GIF;
  }
  const assetPath = extractAssetPath(url);
  if (assetPath === null) {
    return url;
  }
  const existing = extractExistingDirectives(url);
  const directives = buildDirectiveString({
    centerCrop: existing.centerCrop,
    width: options?.width ?? existing.width,
    height: options?.height ?? existing.height,
    q: options?.q ?? existing.q,
  });
  return buildUrl(assetPath, directives);
}

export function centerCropUrl(url?: string, options?: ImageOptions): string {
  if (!url) {
    return PLACEHOLDER_GIF;
  }
  const assetPath = extractAssetPath(url);
  if (assetPath === null) {
    return url;
  }
  const existing = extractExistingDirectives(url);
  const directives = buildDirectiveString({
    centerCrop: true,
    width: options?.width ?? existing.width,
    height: options?.height ?? existing.height,
    q: options?.q ?? existing.q,
  });
  return buildUrl(assetPath, directives);
}

export function centerCropThumbnailUrl(url?: string): string {
  return centerCropUrl(url, { width: 576, height: 384, q: 35 });
}
